import React from "react";

function cssLength(value) {
  return typeof value === "number" ? `${value}px` : value;
}

/**
 * Shared non-modal flyover shell. Features own the trigger and interior while
 * core owns anchored placement, the arrow notch, click-away, and Escape.
 */
export function Flyover({
  open,
  onOpenChange,
  trigger,
  children,
  id,
  role = "dialog",
  ariaLabel,
  ariaLabelledBy,
  ariaDescribedBy,
  side = "right",
  align = "end",
  gap = 16,
  alignOffset = 0,
  width = 320,
  maxHeight = "min(520px, calc(100vh - 1rem))",
  anchorClassName = "",
  panelClassName = "",
  style,
}) {
  const generatedId = React.useId();
  const panelId = id ?? generatedId;
  const anchorRef = React.useRef(null);

  const focusTrigger = React.useCallback(() => {
    const triggers = anchorRef.current?.querySelectorAll("[data-flyover-trigger]") ?? [];
    const ownerTrigger = Array.from(triggers).find((element) => element.dataset.flyoverTrigger === panelId);
    ownerTrigger?.focus();
  }, [panelId]);

  const close = React.useCallback((reason = "dismiss") => {
    onOpenChange(false, reason);
  }, [onOpenChange]);

  const toggle = React.useCallback(() => {
    onOpenChange(!open, "trigger");
  }, [onOpenChange, open]);

  React.useEffect(() => {
    if (!open) return undefined;

    const closeOnPointerDown = (event) => {
      const anchor = anchorRef.current;
      const path = event.composedPath?.();
      if (anchor && (path ? path.includes(anchor) : anchor.contains(event.target))) return;
      close("outside-press");
    };
    const closeOnEscape = (event) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      event.stopPropagation();
      close("escape");
      window.requestAnimationFrame(focusTrigger);
    };

    document.addEventListener("pointerdown", closeOnPointerDown);
    document.addEventListener("keydown", closeOnEscape, true);
    return () => {
      document.removeEventListener("pointerdown", closeOnPointerDown);
      document.removeEventListener("keydown", closeOnEscape, true);
    };
  }, [close, focusTrigger, open]);

  const triggerProps = {
    "aria-controls": open ? panelId : undefined,
    "aria-expanded": open,
    "aria-haspopup": role,
    "data-flyover-trigger": panelId,
  };
  const panelStyle = {
    "--vp-flyover-gap": cssLength(gap),
    "--vp-flyover-align-offset": cssLength(alignOffset),
    "--vp-flyover-width": cssLength(width),
    "--vp-flyover-max-height": cssLength(maxHeight),
    ...style,
  };

  return (
    <div
      ref={anchorRef}
      className={`vp-flyover-anchor ${anchorClassName}`.trim()}
    >
      {trigger({ open, toggle, close, panelId, triggerProps })}
      {open && (
        <div
          className={`vp-flyover ${panelClassName}`.trim()}
          id={panelId}
          role={role}
          aria-label={ariaLabel}
          aria-labelledby={ariaLabelledBy}
          aria-describedby={ariaDescribedBy}
          data-side={side}
          data-align={align}
          style={panelStyle}
        >
          {typeof children === "function" ? children({ close }) : children}
        </div>
      )}
    </div>
  );
}
